import React, { ComponentProps, ReactNode, useState } from "react"
import {
  Flex, Text,
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
} from "@chakra-ui/react"
import { useIntercom } from "react-use-intercom"
import useModal from "services/useModal"
import { ModalName } from "sharedTypes"
import Menu, { MenuDivider, MenuItem } from "components/dialogs/Menu"
import Button from "components/Buttons/Button"
import Icon from "components/elements/Icon"
import WhatsAppIcon from "components/elements/WhatsAppIcon"
import SlidersHorizontalIcon from "components/elements/SlidersHorizontalIcon"
import useFeatureFlag from "services/featureFlags"

type Props = {
  button: ReactNode
  placement?: ComponentProps<typeof Menu>["placement"]
}

const NewTextMenu = ({ button, placement }: Props) => {
  const showModal = useModal()
  const { whatsappMessaging: whatsAppEnabled } = useFeatureFlag()
  const { show: showIntercom } = useIntercom()
  const [betaModalOpen, setBetaModalOpen] = useState(false)

  const handleWhatsAppClick = () => {
    if (whatsAppEnabled) {
      showModal(ModalName.NewTextMessage, { channel: "whatsapp" })
    } else {
      setBetaModalOpen(true)
    }
  }

  const handleContactUs = () => {
    setBetaModalOpen(false)
    showIntercom()
  }

  const betaModal = (
    <Modal isOpen={betaModalOpen} onClose={() => setBetaModalOpen(false)} isCentered size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody py={8} px={6}>
          <Flex flexDirection="column" alignItems="center" textAlign="center" gap={4}>
            <WhatsAppIcon size={10} color="whatsapp.500" />
            <Text fontSize="lg" fontWeight="semibold">WhatsApp Messaging is in Beta</Text>
            <Text color="gray.600">
              Diobox supports bulk WhatsApp messaging with built-in templates
              for registrations, confirmations, data collection, and QR code
              delivery. Contact us to request access.
            </Text>
            <Button onClick={handleContactUs}>Contact Us</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )

  return (
    <>
      <Menu
        button={button}
        placement={placement}
        menuItems={[
          <MenuItem
            key="sms"
            onClick={() => showModal(
              ModalName.NewTextMessage,
              { channel: "sms" },
            )}
          >
            <Flex alignItems="center" gap={2}>
              <Icon icon="text-sms" size={5} color="blue.400" />
              New SMS Message
            </Flex>
          </MenuItem>,
          <MenuDivider key="divider" />,
          <MenuItem key="whatsapp" onClick={handleWhatsAppClick}>
            <Flex alignItems="center" gap={2}>
              <WhatsAppIcon size={5} color="whatsapp.500" />
              New WhatsApp Message
            </Flex>
          </MenuItem>,
          ...(whatsAppEnabled ? [
            <MenuItem
              key="sending-options"
              onClick={() => showModal(ModalName.WhatsAppSendingOptions)}
            >
              <Flex alignItems="center" gap={2}>
                <SlidersHorizontalIcon boxSize={5} color="whatsapp.500" />
                Sending Options
              </Flex>
            </MenuItem>,
          ] : []),
        ]}
      />
      {betaModal}
    </>
  )
}

export default NewTextMenu
